import React, { useEffect, useState } from 'react';
import './AccountDetailsModal.css';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Modal, Spin, Button, Tabs, TabsProps, Row, Col, Flex, Table, Typography, message } from 'antd';
import { gql } from '@apollo/client';
import { useAdminAccess } from '../../../contexts/AdminAccessContext';
import { decodeToken } from 'react-jwt';
import useAccountStatements, { AccountStatement } from '../../../hooks/useAccountStatements';
import { FileTextOutlined } from '@ant-design/icons';
import { mapStatusForClient, EXCLUDED_STATUSES, getStatusClass } from '../../../utils/statusMapping';
import '../../../utils/statusStyles.css';

const GET_ACCOUNT = gql`
  query GetAccount($accountId: Float!) {
    account(id: $accountId) {
      id
      name
      accountNumber
      provider {
        id
        name
        address {
          addressLine1
          addressLine2
          city
          state
          zip
        }
      }
    }
  }
`;

interface AccountDetailsModalProps {
  accountId: number | null;
  visible: boolean;
  onClose: () => void;
}

const AccountDetailsModal: React.FC<AccountDetailsModalProps> = ({ accountId, visible, onClose }) => {
  const { loading, error, data } = useQuery(GET_ACCOUNT, {
    variables: { accountId },
    skip: !accountId
  });
  const { hasAdminAccess } = useAdminAccess();

  const token = localStorage.getItem('accessToken');
  const decoded: any = token ? decodeToken(token) : null;
  const loggedInUserId = decoded?.id;
  const storedUserId = Number(localStorage.getItem('userId'));
  const isImpersonating = hasAdminAccess && loggedInUserId && storedUserId !== Number(loggedInUserId);

  useEffect(() => {
    if (!visible) {
      onClose();
    }
  }, [visible, onClose]);

  const account = data?.account;

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Provider Information',
      children: <>
        <p><strong>Account Name:</strong> {account?.name}</p>
        <p><strong>Account Number:</strong> {account?.accountNumber}</p>
        <p><strong>Address Line 1:</strong> {account?.provider?.address?.addressLine1}</p>
        {account?.provider?.address?.addressLine2 && (
          <p><strong>Address Line 2:</strong> {account?.provider?.address?.addressLine2}</p>
        )}
        <p><strong>City:</strong> {account?.provider?.address?.city}</p>
        <p><strong>State:</strong> {account?.provider?.address?.state}</p>
        <p><strong>Zip Code:</strong> {account?.provider?.address?.zip}</p>
      </>,
    },
    ...(hasAdminAccess && !isImpersonating ? [
      {
        key: '2',
        label: 'Autopay',
        children: <></>
      },
      {
        key: '3',
        label: 'Documents',
        children: <></>
      },
    ] : []),
    {
      key: '4',
      label: 'Activity',
      children: accountId ? <AccountActivity accountId={accountId} /> : <></>
    },
  ];

  return (
    <Modal
      title={account?.name}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {loading ? (
        <Spin />
      ) : error ? (
        <p>Error loading account details.</p>
      ) : (
        account && (
          <>
            <Tabs defaultActiveKey="4" items={items} />
          </>
        )
      )}
    </Modal>
  );
};


// GraphQL query to get account statement download URL
const GET_ACCOUNT_STATEMENT_DOWNLOAD_URL = gql`
  query GetAccountStatementDownloadUrl($id: Float!) {
    accountStatement(id: $id) {
      id
      downloadUrl
    }
  }
`;

// GraphQL query to get account with statements using the correct API structure
const GET_ACCOUNT_WITH_STATEMENTS = gql`
  query GetAccountWithStatements($accountId: Float!) {
    account(id: $accountId) {
      id
      name
      accountStatements(type: "all") {
        id
        accountId
        fileName
        dueDate
        amountDue
        statementBalance
        minimumAmount
        paymentTargetDate
        createdAt
        displayStatus
        downloadUrl
        documentId
        approvalStatus
        isPaid
        paidAmount
        paidDate
        statementDate
        isSkipped
        status
      }
    }
  }
`;

// Component to display account activity (statements)
const AccountActivity: React.FC<{ accountId: number }> = ({ accountId }) => {
  // CRITICAL: Use type: "all" to retrieve ALL statement types (paid, skipped, and unpaid)
  const { loading, error, data } = useQuery(GET_ACCOUNT_WITH_STATEMENTS, {
    variables: { accountId },
    skip: !accountId
  });
  const { hasAdminAccess } = useAdminAccess();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState<AccountStatement | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Query to fetch account statement download URL
  const [getStatementDownloadUrl] = useLazyQuery(GET_ACCOUNT_STATEMENT_DOWNLOAD_URL, {
    onCompleted: (data) => {
      if (data?.accountStatement?.downloadUrl) {
        setDocumentUrl(data.accountStatement.downloadUrl);
      }
      setDocumentLoading(false);
    },
    onError: (error) => {
      setDocumentLoading(false);
    }
  });

  if (loading) return <Spin />;
  if (error) return <Typography.Text type="danger">Error loading account statements.</Typography.Text>;

  const handlePreviewDocument = (statement: AccountStatement) => {
    setSelectedStatement(statement);
    setPreviewVisible(true);
    setDocumentLoading(true);
    setDocumentUrl(null);
    
    // Use the new account statement download URL query
    if (statement.id) {
      getStatementDownloadUrl({ 
        variables: { 
          id: parseInt(statement.id) 
        } 
      });
    } else {
      setDocumentLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
    setSelectedStatement(null);
    setDocumentUrl(null);
  };

  // Filter statements based on admin access and sort by createdAt date (most recent first)
  const filteredStatements = hasAdminAccess 
    ? data?.account?.accountStatements || []
    : data?.account?.accountStatements?.filter(
        (statement: any) => {
          const status = statement.displayStatus || '';
          return !EXCLUDED_STATUSES.some(excluded => 
            status.toLowerCase() === excluded.toLowerCase()
          );
        }
      ) || [];



  const statements = filteredStatements.length > 0
    ? [...filteredStatements].sort((a, b) => {
        if (!a.statementDate) return 1;
        if (!b.statementDate) return -1;
        return new Date(b.statementDate).getTime() - new Date(a.statementDate).getTime();
      })
    : [];

  const columns = [
    {
      title: 'Statement Date',
      dataIndex: 'statementDate',
      key: 'statementDate',
      sorter: (a: AccountStatement, b: AccountStatement) => {
        if (!a.statementDate) return 1;
        if (!b.statementDate) return -1;
        return new Date(a.statementDate).getTime() - new Date(b.statementDate).getTime();
      },
      defaultSortOrder: 'descend' as const,
      render: (date: string | null) => date ? new Date(date).toLocaleDateString('en-US') : ''
    },
    {
      title: 'Payment Target Date',
      dataIndex: 'paymentTargetDate',
      key: 'paymentTargetDate',
      render: (date: string) => date ? new Date(date).toLocaleDateString('en-US') : ''
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => date ? new Date(date).toLocaleDateString('en-US') : ''
    },
    {
      title: 'Statement Balance',
      dataIndex: 'statementBalance',
      key: 'statementBalance',
      render: (amount: number | null) => amount !== null && amount !== undefined ? 
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(amount) : ''
    },
    {
      title: 'Amount Due',
      dataIndex: 'amountDue',
      key: 'amountDue',
      render: (amount: number | null) => amount !== null && amount !== undefined ? 
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(amount) : ''
    },
    {
      title: 'Amount Paid',
      key: 'amountPaid',
      render: (_: any, record: AccountStatement) => record.isPaid && record.paidAmount !== null && record.paidAmount !== undefined ? 
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(record.paidAmount) : ''
    },
    {
      title: 'Payment Date',
      key: 'paymentDate',
      render: (_: any, record: AccountStatement) => record.isPaid && record.paidDate ? 
        new Date(record.paidDate).toLocaleDateString('en-US') : ''
    },
    {
      title: 'Status',
      dataIndex: 'displayStatus',
      key: 'status',
      sorter: (a: AccountStatement, b: AccountStatement) => {
        const statusA = hasAdminAccess ? (a.displayStatus || '') : mapStatusForClient(a.displayStatus, a.status);
        const statusB = hasAdminAccess ? (b.displayStatus || '') : mapStatusForClient(b.displayStatus, b.status);
        return statusA.localeCompare(statusB);
      },
      render: (displayStatus: string | null, record: AccountStatement) => {
        if (!displayStatus && !record.status) return <span></span>;
        
        // Always map status to client-friendly version for client interface
        const mappedStatus = mapStatusForClient(displayStatus, record.status);
        const statusClass = getStatusClass(mappedStatus);
        
        return (
          <span className={statusClass}>
            {mappedStatus}
          </span>
        );
      }
    },
    {
      title: '',
      key: 'action',
      render: (_: any, record: AccountStatement) => (
        <Button 
          type="text" 
          icon={<FileTextOutlined style={{ color: '#1890ff' }} />} 
          onClick={() => handlePreviewDocument(record)}
          title="View Statement"
        />
      )
    }
  ];

  return (
    <div>
      <Typography.Title level={5} style={{ marginBottom: 16 }}>Account Statements</Typography.Title>
      <Table 
        dataSource={statements.map(statement => ({...statement, key: statement.id}))} 
        columns={columns} 
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: statements.length,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} statements`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) {
              setPageSize(size);
              setCurrentPage(1);
            }
          },
          onShowSizeChange: (current, size) => {
            setPageSize(size);
            setCurrentPage(1);
          }
        }}
        size="small"
        rowClassName={(record, index) => index % 2 === 0 ? '' : 'table-row-dark'}
        className="account-activity-table"
        style={{ 
          border: '1px solid #f0f0f0',
          borderRadius: '4px',
          overflow: 'hidden'
        }}
      />

      {/* Document Preview Modal */}
      <Modal
        title={`Statement Preview - ${selectedStatement ? new Date(selectedStatement.createdAt || '').toLocaleDateString('en-US') : ''}`}
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={[
          <Button key="close" onClick={handleClosePreview}>
            Close
          </Button>
        ]}
        width={800}
      >
        {documentLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spin tip="Loading document..." />
          </div>
        ) : documentUrl ? (
          <iframe
            src={documentUrl}
            title={`Statement - ${selectedStatement?.id}`}
            style={{ width: '100%', height: '600px', border: '1px solid #f0f0f0' }}
          />
        ) : selectedStatement && (
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <Typography.Title level={4}>Statement Details</Typography.Title>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <strong>Statement Date:</strong> {selectedStatement.createdAt ? new Date(selectedStatement.createdAt).toLocaleDateString('en-US') : ''}
              </div>
              <div>
                <strong>Due Date:</strong> {selectedStatement.dueDate ? new Date(selectedStatement.dueDate).toLocaleDateString('en-US') : ''}
              </div>
              <div>
                <strong>Statement Balance:</strong> {selectedStatement.statementBalance !== null && selectedStatement.statementBalance !== undefined ? 
                  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(selectedStatement.statementBalance) : ''}
              </div>
              <div>
                <strong>Amount Due:</strong> {selectedStatement.amountDue !== null && selectedStatement.amountDue !== undefined ? 
                  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(selectedStatement.amountDue) : ''}
              </div>
              <div>
                <strong>Status:</strong> <span className={
                  selectedStatement.displayStatus?.toLowerCase().includes('pending') ? 'ant-table-cell-status-pending' : 
                  selectedStatement.displayStatus?.toLowerCase() === 'paid' ? 'ant-table-cell-status-paid' : ''
                }>{mapStatusForClient(selectedStatement.displayStatus, selectedStatement.status)}</span>
              </div>
            </div>
            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <Typography.Text type="secondary">
                Document preview is not available. Please contact support if you need to view this statement.
              </Typography.Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AccountDetailsModal;
